import type { BannerPosition, ShipstarConfig, ShipstarInitOptions, WidgetType } from "./types";

const DEFAULTS = {
  type: "changelog" as WidgetType,
  apiBaseUrl: "https://api.shipstar.ai",
  theme: "auto" as const,
  locale: "en",
  maxItems: 10,
  branding: true,
  subscribe: true,
  search: false,
  initialArticle: null as string | null,
  onNavigate: null as ((slug: string) => void) | null,
  position: "top" as BannerPosition,
  dismissible: true,
  debug: false,
  onLoad: null,
  onError: null,
};

export function readConfigFromScript(
  el: HTMLScriptElement
): ShipstarConfig | null {
  const apiKey = el.getAttribute("data-shipstar-key");
  if (!apiKey) {
    console.error("[Shipstar] Missing required attribute: data-shipstar-key");
    return null;
  }

  const debugAttr = el.getAttribute("data-debug");
  let debug = DEFAULTS.debug;
  if (debugAttr !== null) {
    debug = debugAttr === "" || debugAttr === "true";
  }

  const maxItemsAttr = el.getAttribute("data-max-items");
  const maxItems = maxItemsAttr ? parseInt(maxItemsAttr, 10) : DEFAULTS.maxItems;

  const theme = (el.getAttribute("data-theme") as ShipstarConfig["theme"]) || DEFAULTS.theme;
  const type = (el.getAttribute("data-type") as WidgetType) || DEFAULTS.type;
  const position: BannerPosition =
    el.getAttribute("data-position") === "inline" ? "inline" : DEFAULTS.position;

  return {
    apiKey,
    type,
    apiBaseUrl: el.getAttribute("data-api-url") || DEFAULTS.apiBaseUrl,
    containerId: el.getAttribute("data-container") || null,
    theme,
    locale: el.getAttribute("data-locale") || DEFAULTS.locale,
    maxItems: isNaN(maxItems) ? DEFAULTS.maxItems : maxItems,
    branding: el.getAttribute("data-branding") !== "false",
    subscribe: el.getAttribute("data-subscribe") !== "false",
    search: el.getAttribute("data-search") === "true",
    initialArticle: el.getAttribute("data-initial-article") || null,
    onNavigate: null,
    position,
    dismissible: el.getAttribute("data-dismissible") !== "false",
    debug,
    onLoad: null,
    onError: null,
  };
}

export function readConfigFromObject(
  opts: ShipstarInitOptions
): ShipstarConfig {
  if (!opts.apiKey) {
    throw new Error("[Shipstar] Missing required option: apiKey");
  }

  return {
    apiKey: opts.apiKey,
    type: opts.type || DEFAULTS.type,
    apiBaseUrl: opts.apiBaseUrl || DEFAULTS.apiBaseUrl,
    containerId: null,
    theme: opts.theme || DEFAULTS.theme,
    locale: opts.locale || DEFAULTS.locale,
    maxItems: opts.maxItems ?? DEFAULTS.maxItems,
    branding: opts.branding ?? DEFAULTS.branding,
    subscribe: opts.subscribe ?? DEFAULTS.subscribe,
    search: opts.search ?? DEFAULTS.search,
    initialArticle: opts.initialArticle ?? DEFAULTS.initialArticle,
    onNavigate: opts.onNavigate ?? DEFAULTS.onNavigate,
    position: opts.position === "inline" ? "inline" : DEFAULTS.position,
    dismissible: opts.dismissible ?? DEFAULTS.dismissible,
    debug: opts.debug ?? DEFAULTS.debug,
    onLoad: opts.onLoad || null,
    onError: opts.onError || null,
  };
}
